import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/base/Button';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      setIsVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      setIsVisible(false);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
    setIsVisible(false);
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  if (!isVisible || isInstalled) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl border border-background-200 p-4 md:p-5">
        <div className="flex items-start gap-3 md:gap-4">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
            <span className="text-2xl md:text-3xl">
              <img src="images/logo.png" alt="" />
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm md:text-base font-heading font-bold text-foreground-900 mb-1">
              Installez YAATOU GS 
            </h3>
            <p className="text-xs md:text-sm text-foreground-600 mb-3 md:mb-4">
              Ajoutez l'application à votre écran d'accueil pour un accès rapide et une meilleure expérience.
            </p>
            <div className="flex gap-2">
              <Button variant="accent" size="sm" className="flex-1" onClick={handleInstall}>
                <span className="flex items-center justify-center gap-1.5">
                  <i className="ri-download-line text-sm" />
                  Installer
                </span>
              </Button>
              <Button variant="secondary" size="sm" onClick={handleDismiss}>
                Plus tard
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-foreground-400 hover:text-foreground-600 transition-colors shrink-0 cursor-pointer"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
      </div>
    </div>
  );
}
